import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Box, Column, Row, Text } from '~/commonComponents';
import {
  CandidateStatus,
  CandidateStatusMapper,
  ICandidateListViewEntity,
  useToggleShortlistCandidate,
} from '~/domain/PermanentJobs';
import DefaultProfileImage from '~/images/doctor.png';
import {
  ArrowRightCircleOutlineIcon,
  BookmarkOutlineIcon,
  BookmarkSolidIcon,
  ChatOutlineIcon,
  CheckCircleOutlineIcon,
  CloseCircleOutlineIcon,
} from '~/images/icons';
import { Colors } from '~/themes/colors';
import './ListItem.style.scss';
import {
  firePermCandidateListActionClicked,
  firePermCandidateListCandidateClicked,
  firePermCandidateListShortlistClicked,
} from '~/posthogs/events';
import { CandidateAction } from '~/posthogs/events/PermJobs/types';

interface IListItemProps {
  data: ICandidateListViewEntity;
  jobPostingId: string;
  onHireClick?: (candidate: Pick<ICandidateListViewEntity, 'id' | 'professional'>) => void;
  onRejectClick?: (candidate: Pick<ICandidateListViewEntity, 'id' | 'professional'>) => void;
}

export const ListItem = ({
  data: {
    createdAt,
    id,
    isShortlisted,
    professional,
    officeStatus,
    onsiteInterviewId,
    yearsOfExperience,
  },
  jobPostingId,
  onHireClick,
  onRejectClick,
}: IListItemProps) => {
  const navigate = useNavigate();

  const { mutate: doShortlistCandidate, isPending: isShortlistCandidatePending } =
    useToggleShortlistCandidate({
      candidateId: id,
      isShortlisted,
    });

  const isHired = officeStatus === CandidateStatus.Hired;
  const isInterviewed = officeStatus === CandidateStatus.Interviewed;
  const isRejected = officeStatus === CandidateStatus.Rejected;
  const isScheduled = officeStatus === CandidateStatus.Scheduled;

  const canShortlist = !isHired && !isRejected;

  const StatusActions = useMemo(() => {
    if (isHired) {
      return (
        <Text bold fontSize={18} marginLeft={40}>
          Hired
        </Text>
      );
    }

    if (isRejected) {
      return (
        <Text bold fontSize={18} marginLeft={40}>
          Rejected
        </Text>
      );
    }

    if (isScheduled) {
      return (
        <button
          className="btn-message"
          data-cy="candidate-action-message"
          onClick={() => {
            // Track the message action
            firePermCandidateListActionClicked({
              postingId: jobPostingId,
              professionalId: professional.id,
              actionName: CandidateAction.Message,
            });
            navigate(`/dashboard/chat/${onsiteInterviewId}`);
          }}
        >
          <ChatOutlineIcon color={Colors.secondary_500} />
          <Text bold fontSize={18}>
            Message
          </Text>
        </button>
      );
    }

    return (
      <>
        <button
          className="btn-reject"
          data-cy="candidate-action-reject"
          onClick={() => {
            // Track the reject action
            firePermCandidateListActionClicked({
              postingId: jobPostingId,
              professionalId: professional.id,
              actionName: CandidateAction.Reject,
            });
            onRejectClick?.({ id, professional });
          }}
        >
          <CloseCircleOutlineIcon color={Colors.warning_500} />
          <Text bold fontSize={18}>
            Reject
          </Text>
        </button>
        {isInterviewed ? (
          <button
            className="btn-hire"
            data-cy="candidate-action-hire"
            onClick={() => {
              // Track the hire action
              firePermCandidateListActionClicked({
                postingId: jobPostingId,
                professionalId: professional.id,
                actionName: CandidateAction.Hire,
              });
              onHireClick?.({ id, professional });
            }}
          >
            <CheckCircleOutlineIcon />
            <Text bold fontSize={18}>
              Hire
            </Text>
          </button>
        ) : (
          <button
            className="btn-review"
            data-cy="candidate-action-review"
            onClick={() => {
              // Track the review action
              firePermCandidateListActionClicked({
                postingId: jobPostingId,
                professionalId: professional.id,
                actionName: CandidateAction.Review,
              });
              navigate(`/dashboard/job/${onsiteInterviewId}/confirm/${id}`);
            }}
          >
            <ArrowRightCircleOutlineIcon color={Colors.secondary_500} />
            <Text bold fontSize={18}>
              Review
            </Text>
          </button>
        )}
      </>
    );
  }, [
    isHired,
    isRejected,
    isScheduled,
    isInterviewed,
    navigate,
    onsiteInterviewId,
    onRejectClick,
    id,
    professional,
    onHireClick,
    jobPostingId,
  ]);

  return (
    <Row
      alignItems="center"
      backgroundColor={Colors.white}
      borderRadius={10}
      className="cursor-pointer shadow-xs hover__shadow-sm"
      display="grid"
      gridTemplateColumns="96px 1fr 15% 12% 25%"
      data-cy="pj-candidate-list-item"
      key={id}
      paddingBlock={12}
    >
      <Box
        alignItems="flex-end"
        justifyContent="center"
        height="100%"
        style={{ cursor: 'pointer' }}
        onClick={() => {
          // Track candidate card click
          firePermCandidateListCandidateClicked({
            postingId: jobPostingId,
            professionalId: professional.id,
            candidateStatus: officeStatus,
          });
          navigate(`/dashboard/professional/${professional.id}`);
        }}
      >
        <Box
          alignItems="center"
          backgroundColor="#DFEBED"
          borderRadius="100%"
          height={48}
          justifyContent="center"
          justifySelf="flex-end"
          marginRight={16}
          width={48}
        >
          {professional.picture ? (
            <img
              src={professional.picture}
              height="100%"
              width="100%"
              style={{
                borderRadius: 100,
                objectFit: 'cover',
              }}
            />
          ) : (
            <img src={DefaultProfileImage} height={36} width={36} />
          )}
        </Box>
      </Box>
      <Column
        style={{ cursor: 'pointer' }}
        onClick={() => {
          // Track candidate card click
          firePermCandidateListCandidateClicked({
            postingId: jobPostingId,
            professionalId: professional.id,
            candidateStatus: officeStatus,
          });
          navigate(`/dashboard/professional/${professional.id}`);
        }}
      >
        <Text bold fontSize={18} textDecoration="underline">
          {professional.name}
        </Text>
        <Text color={Colors.neutral_500}>
          <Text bold color={Colors.neutral_500}>
            Experience
          </Text>
          : {yearsOfExperience} years
        </Text>
        <Text color={Colors.neutral_500}>
          <Text bold color={Colors.neutral_500}>
            Applied on
          </Text>
          : {createdAt}
        </Text>
      </Column>
      <Text color={Colors.neutral_500} data-cy="candidate-process-step">{CandidateStatusMapper.toProcessStep(officeStatus)}</Text>
      <button
        disabled={!canShortlist || isShortlistCandidatePending}
        style={{
          background: 'transparent',
        }}
        onClick={() => {
          // Track shortlist click
          firePermCandidateListShortlistClicked({
            postingId: jobPostingId,
            isShortlisted: !isShortlisted, // The new state after toggle
          });
          doShortlistCandidate();
        }}
      >
        {isShortlisted ? (
          <BookmarkSolidIcon color={canShortlist ? Colors.secondary_500 : Colors.secondary_200} />
        ) : (
          <BookmarkOutlineIcon color={canShortlist ? Colors.secondary_500 : Colors.secondary_200} />
        )}
      </button>
      <Row gap={12}>{StatusActions}</Row>
    </Row>
  );
};
